import { Box, IconButton, Switch } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { ChangeEvent, useCallback, useState } from 'react';

import { appActions } from 'app/app.actions';
import { ListItemHeader } from 'components/settings/components/sections/openingHours/components/listItemHeader/listItemHeader';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { IntervalsSelect } from '../../../intervalsSelect/intervalsSelect';
import type { ListItemComponentProps } from '../../listItem';
import { useListItemContext } from '../../listItem.context';
import styles from '../../listItem.module.scss';
import { OverriddenItem } from '../overriddenItem/overriddenItem';

export const OpeningHours = ({
  className,
  hasConflict,
  index,
  itemClassName,
  openingHours,
  overrideException,
}: ListItemComponentProps): JSX.Element => {
  const {
    duration,
    id,
    intervals,
    name,
    next_date,
    opening_hour_type: openingHourType,
    recurring,
  } = openingHours;

  const {
    intervalsSelectProps: {
      errors,
      onChange: onIntervalsChange,
      validate: onIntervalsValidate,
    },
    onDelete,
    onOverriddenChange,
  } = useListItemContext();

  const [isClosed, setIsClosed] = useState(!intervals?.length);
  const dispatch = useAppDispatch();

  const handleSwitchChange = useCallback(
    ({ target: { checked } }: ChangeEvent<HTMLInputElement>) =>
      void setIsClosed(!checked),
    [],
  );

  const handleDeleteClick = useCallback(async () => {
    if (!onDelete) return;

    const { isConfirmed, isCancelled } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete exception?',
            description: `Are you sure want to delete the exception "${name}"`,
            confirm: {
              variant: 'danger',
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      onDelete(id)();
      ampli.businessHoursScheduleDeleteExceptionFormSubmitted();
    } else if (isCancelled) {
      ampli.businessHoursScheduleDeleteExceptionFormCancelled();
    }
  }, [id, name, dispatch, onDelete]);

  return (
    <div className={classNames(styles.root, className)}>
      {overrideException && onOverriddenChange ? (
        <OverriddenItem
          openingHours={openingHours}
          overrideException={overrideException}
          onChange={() => onOverriddenChange(openingHours)}
        />
      ) : (
        <div
          className={classNames(
            styles.item,
            styles.itemStandard,
            {
              [styles.itemIsRegular]: openingHourType === 'regular',
              [styles.itemIsClosed]: isClosed,
              [styles.itemHasConflict]: hasConflict,
            },
            itemClassName,
          )}
        >
          <ListItemHeader
            duration={duration}
            hasConflict={hasConflict}
            name={name}
            nextDate={next_date}
            openingHourType={openingHourType}
            recurring={recurring}
          />
          <Box className={styles.timeRanges}>
            <IntervalsSelect
              errors={errors[index] || []}
              intervals={intervals}
              isClosed={isClosed}
              onChange={onIntervalsChange(id)}
              validate={onIntervalsValidate(index)}
            />
          </Box>
          <Box>
            <Switch
              checked={!isClosed}
              className={styles.switch}
              name="open-switch"
              value=""
              onChange={handleSwitchChange}
            />
          </Box>
        </div>
      )}
      {openingHourType !== 'regular' && onDelete && (
        <div className={styles.iconButtonWrapper}>
          <IconButton
            icon="trash"
            onClick={() => {
              ampli.businessHoursScheduleExceptionOptionsMenuOptionDeleteClicked(
                { openingHourType },
              );
              handleDeleteClick();
            }}
            variant="tertiary"
          />
        </div>
      )}
    </div>
  );
};
